import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '@/App';
import {
  Youtube,
  Facebook,
  Instagram,
  Music2,
  Check,
  X,
  RefreshCw,
  Link as LinkIcon,
  AlertCircle
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORMS = [
  {
    id: 'youtube',
    name: 'YouTube',
    icon: Youtube,
    color: '#FF0000',
    description: 'Post long-form videos and Shorts'
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: Facebook,
    color: '#1877F2',
    description: 'Post to Facebook Pages'
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: Instagram,
    color: '#E4405F',
    description: 'Post Reels and Feed videos'
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    icon: Music2,
    color: '#000000',
    description: 'Post short-form videos'
  }
];

const ConnectedPlatforms = () => {
  const { user } = useContext(AuthContext);
  const [creators, setCreators] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState('');
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCreators();
  }, []);

  useEffect(() => {
    if (selectedCreator) {
      fetchConnections();
    }
  }, [selectedCreator]);

  const fetchCreators = async () => {
    try {
      const orgsRes = await axios.get(`${API}/organizations`, { withCredentials: true });
      if (orgsRes.data.length > 0) {
        const creatorsRes = await axios.get(`${API}/creators?org_id=${orgsRes.data[0].id}`, { withCredentials: true });
        setCreators(creatorsRes.data);
        if (creatorsRes.data.length > 0) {
          setSelectedCreator(creatorsRes.data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch creators:', error);
    }
  };

  const fetchConnections = async () => {
    try {
      const response = await axios.get(`${API}/platforms/connections/${selectedCreator}`, { withCredentials: true });
      setConnections(response.data);
    } catch (error) {
      console.error('Failed to fetch connections:', error);
    }
  };

  const handleConnect = async (platform) => {
    setLoading(true);
    try {
      // Get OAuth URL
      const response = await axios.get(`${API}/platforms/oauth-url/${platform}?creator_id=${selectedCreator}`, {
        withCredentials: true
      });
      
      // In production, this would open OAuth popup and handle callback
      // For demo, we'll simulate the callback
      const mockCode = `demo_code_${Date.now()}`;
      
      await axios.post(`${API}/platforms/oauth-callback/${platform}`, {
        code: mockCode,
        state: response.data.state
      }, { withCredentials: true });
      
      toast.success(`${platform} connected successfully!`);
      fetchConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to connect ${platform}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (connectionId) => {
    if (!window.confirm('Are you sure you want to disconnect this platform?')) return;
    
    try {
      await axios.delete(`${API}/platforms/connection/${connectionId}`, { withCredentials: true });
      toast.success('Platform disconnected');
      fetchConnections();
    } catch (error) {
      toast.error('Failed to disconnect platform');
    }
  };

  const handleRefreshToken = async (connectionId) => {
    try {
      await axios.post(`${API}/platforms/refresh-token/${connectionId}`, {}, { withCredentials: true });
      toast.success('Token refreshed successfully');
      fetchConnections();
    } catch (error) {
      toast.error('Failed to refresh token');
    }
  };

  const getConnection = (platformId) => {
    return connections.find(c => c.platform === platformId && c.is_active);
  };

  const isExpiringSoon = (connection) => {
    if (!connection || !connection.expires_at) return false;
    const expiresAt = new Date(connection.expires_at);
    const now = new Date();
    const hoursUntilExpiry = (expiresAt - now) / (1000 * 60 * 60);
    return hoursUntilExpiry < 24 && hoursUntilExpiry > 0;
  };

  const isExpired = (connection) => {
    if (!connection || !connection.expires_at) return false;
    return new Date(connection.expires_at) < new Date();
  };

  return (
    <div className="p-8 space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-white mb-2">Connected Platforms</h1>
        <p className="text-[#8A93A6] text-lg">Connect your social media accounts to enable posting</p>
      </div>

      {/* Creator Selector */}
      <div className="glass rounded-2xl p-6">
        <label className="block text-sm font-medium text-[#8A93A6] mb-2">Select Creator</label>
        <Select value={selectedCreator} onValueChange={setSelectedCreator}>
          <SelectTrigger className="max-w-md">
            <SelectValue placeholder="Select creator" />
          </SelectTrigger>
          <SelectContent>
            {creators.map(creator => (
              <SelectItem key={creator.id} value={creator.id}>{creator.display_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Platform Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {PLATFORMS.map((platform) => {
          const connection = getConnection(platform.id);
          const connected = !!connection;
          const expiringSoon = isExpiringSoon(connection);
          const expired = isExpired(connection);
          const PlatformIcon = platform.icon;

          return (
            <div
              key={platform.id}
              className={`glass rounded-2xl p-6 border-2 ${
                connected && !expired
                  ? 'border-[#29E07F]/30'
                  : expired
                  ? 'border-[#FF4F4F]/30'
                  : 'border-white/10'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${platform.color}20` }}
                  >
                    <PlatformIcon size={24} style={{ color: platform.color }} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{platform.name}</h3>
                    <p className="text-sm text-[#8A93A6]">{platform.description}</p>
                  </div>
                </div>

                {connected && !expired ? (
                  <div className="flex items-center gap-2">
                    <Check size={20} className="text-[#29E07F]" />
                    <span className="text-sm text-[#29E07F]">Connected</span>
                  </div>
                ) : expired ? (
                  <div className="flex items-center gap-2">
                    <X size={20} className="text-[#FF4F4F]" />
                    <span className="text-sm text-[#FF4F4F]">Expired</span>
                  </div>
                ) : null}
              </div>

              {connected && !expired && (
                <div className="mb-4 p-3 rounded-xl bg-white/5">
                  <div className="flex items-center gap-2 text-sm text-[#8A93A6] mb-2">
                    <LinkIcon size={16} />
                    <span>Connected as: {connection.platform_username || 'User'}</span>
                  </div>
                  {expiringSoon && (
                    <div className="flex items-center gap-2 text-sm text-[#FFA500] mt-2">
                      <AlertCircle size={16} />
                      <span>Token expires soon</span>
                    </div>
                  )}
                  {connection.last_error && (
                    <div className="flex items-center gap-2 text-sm text-[#FF4F4F] mt-2">
                      <AlertCircle size={16} />
                      <span>{connection.last_error}</span>
                    </div>
                  )}
                </div>
              )}

              {expired && connection && (
                <div className="mb-4 p-3 rounded-xl bg-[#FF4F4F]/10 border border-[#FF4F4F]/20">
                  <div className="flex items-center gap-2 text-sm text-[#FF4F4F]">
                    <AlertCircle size={16} />
                    <span>Connection expired. Please reconnect.</span>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                {!connected || expired ? (
                  <button
                    onClick={() => handleConnect(platform.id)}
                    disabled={loading}
                    className="flex-1 py-3 rounded-lg bg-[#3A7BFF] hover:bg-[#3A7BFF]/80 text-white font-medium disabled:opacity-50"
                  >
                    {loading ? 'Connecting...' : 'Connect'}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleRefreshToken(connection.id)}
                      className="flex-1 py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium flex items-center justify-center gap-2"
                    >
                      <RefreshCw size={18} />
                      Refresh Token
                    </button>
                    <button
                      onClick={() => handleDisconnect(connection.id)}
                      className="flex-1 py-3 rounded-lg bg-[#FF4F4F]/20 hover:bg-[#FF4F4F]/30 text-[#FF4F4F] font-medium"
                    >
                      Disconnect
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Section */}
      <div className="glass rounded-2xl p-6">
        <h3 className="text-lg font-bold text-white mb-3">How It Works</h3>
        <div className="space-y-2 text-sm text-[#8A93A6]">
          <p>• Connect your platforms once to enable automated posting</p>
          <p>• Tokens are refreshed automatically to keep connections active</p>
          <p>• Use Scheduler to schedule posts or AutoPilot to post automatically</p>
          <p>• Disconnect anytime to revoke access</p>
        </div>
      </div>
    </div>
  );
};

export default ConnectedPlatforms;
